// Which language this build teaches, and which language it teaches it in.
//
// Those are two different facts and Mynah had only ever needed one of them, because it was
// Mandarin explained in English and every string said so out loud. The second pair makes the
// difference matter: English explained in Chinese is the same app with the two swapped, and
// nothing about the scheduler, the decks, the drills or the audio cares which way round they are.
//
// THIS FILE IS A DECLARATION, NOT A TRANSLATION LAYER. It carries the handful of nouns the app has
// to put in a sentence — what the language is called, what its readings are called, what its level
// labels are called — so that those sentences can be written once. Translating the app's own
// prose into another language is a different job (MYN-100), and this is what it will be built on.

export interface Language {
  /** BCP-47. What speechSynthesis is asked for, and what `lang` on the document says. */
  code: string;
  /** What it is called in the language the app explains things in. "Mandarin". */
  name: string;
  /** What it calls itself. "中文" — shown beside the greeting, where it belongs to the learner. */
  endonym: string;
  /**
   * The everyday word for the language, used both before a noun and in running prose: a "Chinese"
   * voice, and "English turned into Chinese".
   *
   * A separate field because English does not derive it from `name` — Mandarin/Chinese,
   * Spanish/Spanish, Dutch/Dutch. `name` is what the course IS, and is what belongs in a title:
   * "Mandarin flashcards", not "Chinese flashcards". This is what belongs in a sentence.
   */
  everyday: string;
}

export interface Pair {
  version: number;
  /** The language being learned. Everything on the front of a card is in this one. */
  learning: Language;
  /** The language it is explained in. Everything on the back, and the app's own prose. */
  explainedIn: Language;
  /**
   * What this language's pronunciation aid is called — pinyin, romaji, IPA.
   *
   * Null for a pair that has none: an English deck for a Spanish speaker shows the word and that
   * is the whole of it, and the app must not offer to hide something it never shows.
   */
  reading: { name: string; Name: string } | null;
  /**
   * What the level labels are called, and what the top band reads as. Null when there are none.
   *
   * `labels` overrides the formatting for schemes whose bands are not "<name> <number>". HSK is:
   * band 3 is "HSK 3". The Japanese one is not, twice over — its bands count the other way (1 is
   * the HARDEST), and the only published data is the pre-2010 test's, which maps onto today's
   * N-levels everywhere except old level 2, which the source itself refuses to split between N2
   * and N3. So the label for each band is written down rather than computed (MYN-166).
   */
  level: { name: string; top: string; labels?: Record<string, string> } | null;
  /**
   * A second script shown beside the first, when the language has one worth showing.
   *
   * Simplified and traditional Chinese are the case in hand. Null everywhere else, and then the
   * setting that offers it is not there at all rather than being there and doing nothing.
   */
  altScript: { name: string; alongside: string } | null;
  /** What one item of the writing system is called: a character, a letter, a syllable. */
  unit: { one: string; many: string };
  /**
   * A handful of marks in the language being LEARNED, tiled faintly behind the whole app.
   *
   * Decoration, and that is exactly why it belongs to the course rather than to the stylesheet:
   * the characters were baked into body::after as 词 说 问 字 学 读, so a learner of Japanese read
   * 質問する over a Chinese wallpaper and a Chinese speaker learning English got Chinese there too
   * (MYN-161). Nobody is meant to read them -- they are at 2% opacity -- but a wallpaper in the
   * wrong language is the sort of thing that is felt before it is noticed.
   *
   * Optional, because a corpus downloaded from an older build has no such field, and a missing
   * wallpaper must mean no wallpaper rather than a broken render.
   */
  wallpaper?: string[];
  /**
   * Two or three single marks of the writing system, laid over the home illustrations.
   *
   * The first two lift off the book in the Words scene, the third sits under the nib in the Made
   * one. Separate from `wallpaper` because they are doing a different job: the wallpaper tiles six
   * marks behind everything, while these sit inside a 120x92 drawing at 12-15px, so a course whose
   * marks are WORDS -- English is one -- wants single letters here and whole words there.
   *
   * Optional, and an absent list draws the scene without them (MYN-162).
   */
  glyphs?: string[];
  /**
   * Which of this course's words each hand-drawn picture belongs to: word -> picture id.
   *
   * A picture is of a THING -- a cat, somebody running -- so it cannot be keyed to one language's
   * spelling. web/word-art.ts held 31 of them keyed by the Chinese word and looked them up with
   * the learned word's surface form, which reached all 31 in zh-en, 9 in ja-en by coincidence of
   * shared kanji, and none at all in the two courses that teach English (MYN-173).
   *
   * DECLARED RATHER THAN INFERRED. Matching English glosses at runtime was the cheap option and it
   * is wrong: it gave 所, a place, the picture of putting something down. A wrong picture teaches a
   * meaning the word does not have, silently, to somebody with no way to check -- which is worse
   * than no picture, and no picture is what this app already does for the words it cannot draw.
   *
   * Optional, and a course without one simply shows no pictures.
   */
  pictures?: Record<string, string>;
}

/**
 * The pair, before one has loaded.
 *
 * Mandarin explained in English, which is what this app was for its whole first life. It exists so
 * that a render which happens before the data arrives says something true rather than "undefined",
 * and so a test does not have to build one by hand to ask a question about a string.
 */
export const DEFAULT_PAIR: Pair = {
  version: 1,
  learning: { code: 'zh-CN', name: 'Mandarin', endonym: '中文', everyday: 'Chinese' },
  explainedIn: { code: 'en', name: 'English', endonym: 'English', everyday: 'English' },
  reading: { name: 'pinyin', Name: 'Pinyin' },
  level: { name: 'HSK', top: 'HSK 7–9' },
  altScript: { name: 'traditional characters', alongside: 'simplified' },
  unit: { one: 'character', many: 'characters' },
  wallpaper: ['词', '说', '问', '字', '学', '读'],
  glyphs: ['字', '词', '写'],
  pictures: {
    人: 'person', 孩子: 'child', 朋友: 'friend', 手: 'hand', 心: 'heart', 眼睛: 'eye',
    看: 'see', 听: 'listen', 说: 'say', 问: 'ask', 走: 'walk', 跑: 'run', 来: 'come',
    去: 'go', 车: 'car', 吃: 'eat', 饭: 'meal', 水: 'water', 钱: 'money', 买: 'buy',
    家: 'home', 学校: 'school', 月: 'moon', 书: 'book', 拿: 'take', 放: 'put',
    开: 'open', 雨: 'rain', 天: 'sky', 猫: 'cat', 狗: 'dog',
  },
};

/** Does a voice speak the language being learned? Compared on the primary subtag, so a zh-TW
 *  voice still counts as Chinese — an accent is not a different language. */
export function speaksLearning(pair: Pair, lang: string): boolean {
  const want = pair.learning.code.split('-')[0]!.toLowerCase();
  return lang.toLowerCase().split('-')[0] === want;
}

/**
 * Put the pair's own nouns into the shell, and take out what this pair does not have.
 *
 * Two jobs, and the second is the one that was missed first time round. Most languages have no
 * pinyin, no HSK and no second script, so the settings that offer those must not be there — a row
 * reading "Show {{reading.name}} on cards" is worse than anything it could have said. An element
 * carrying `data-pair-needs="reading"` is hidden when that part of the pair is null.
 *
 * Unresolved placeholders are then blanked rather than left visible. That is only safe because
 * test/pair.test.ts asserts the built shell contains no `{{` at all, so a key somebody forgot to
 * add fails the build rather than shipping an empty sentence.
 */
/**
 * One of the pair's own nouns, by the dotted path a placeholder names.
 *
 * The empty string for anything missing, which is what makes `{{reading.name}}` safe on a pair
 * with no readings: the row carrying it is hidden by `data-pair-needs` and never read, and if one
 * ever escaped that it would say nothing rather than say "undefined".
 */
export function pairValue(pair: Pair, path: string): string {
  const value = path
    .split('.')
    .reduce<unknown>((o, k) => (o == null ? o : (o as Record<string, unknown>)[k]), pair);
  return typeof value === 'string' ? value : '';
}

/** Does this pair have the part an element says it needs? */
export const pairHas = (pair: Pair, need: string): boolean => Boolean(pair[need as keyof Pair]);

export function fillPair(html: string, pair: Pair): string {
  const out = html.replace(
    /<([a-z]+)([^>]*\bdata-pair-needs="([a-zA-Z]+)"[^>]*)>/gu,
    (whole, tag: string, attrs: string, need: string) =>
      (pairHas(pair, need) ? whole : `<${tag}${attrs} hidden>`),
  );
  return out.replace(/\{\{([a-zA-Z.]+)\}\}/gu, (_whole, path: string) => pairValue(pair, path));
}

/**
 * The course that teaches `learning` and explains it in `explainedIn`, if anybody has built one.
 *
 * A course is a PAIR, but a learner makes two independent choices: what to study, in Settings, and
 * what language the app speaks, in the bar at the top. The pair is derived from those two -- which
 * is why this exists as a lookup rather than as a list of permutations for somebody to scroll
 * (MYN-171).
 *
 * NULL IS A REAL ANSWER AND THE INTERESTING ONE. Only four of the six ordered pairs over English,
 * Chinese and Japanese have a corpus: there is no Japanese explained in Chinese, and none the
 * other way. So "learning Japanese, reading the app in Chinese" asks for something nobody has
 * built, and the caller has to say so rather than strand a learner on a course that cannot load.
 */
export function courseWith<T extends { id: string; pair: Pair }>(
  courses: readonly T[],
  learning: string,
  explainedIn: string,
): T | null {
  return (
    courses.find(
      (c) => c.pair.learning.code === learning && c.pair.explainedIn.code === explainedIn,
    ) ?? null
  );
}

/** Every language a course could be studied in, given the language the app is speaking. */
export function learnableIn<T extends { pair: Pair }>(
  courses: readonly T[],
  explainedIn: string,
): T[] {
  return courses.filter((c) => c.pair.explainedIn.code === explainedIn);
}
